import moment from "moment";

export type ISODateString = string;

/**
 * Returns a string of a given date without a timestamp.
 * @param {Date | null | undefined} date
 */
export function dateToString(date: Date | null | undefined): ISODateString | null {
    // we need the string date format to be yyyy-mm-dd
    if (date) {
        const isoDateString = moment(date).format("YYYY-MM-DD");
        assertISODateString(isoDateString);
        return isoDateString as ISODateString;
    }

    return null;
}

function assertISODateString(value: string): asserts value is ISODateString {
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!isoDatePattern.test(value)) {
        throw new Error(`Invalid ISO date string: ${value}`);
    }
}

/**
 * Returns a formatted string representing the time portion of a Date.
 * @param {Date | null | undefined} date
 */
export function timeToString(date: Date | null | undefined): string {
    if (date) {
        return moment(date).format("hh:mm A");
    }

    return "";
}

/**
 * Returns a string of a given date with a timestamp.
 * @param {Date | null} date
 */
export function dateWithTimeToString(date: Date | null): string {
    // we need the string date format to be yyyy-mm-dd
    if (date !== null) {
        return moment(date).format("YYYY-MM-DD h:mm:ss A");
    }

    return "";
}

/**
 * Converts a string to a date.
 * @param {string} date
 */
export function getDateFromString(date: string | null): Date {
    return moment(date).toDate();
}

/**
 * Returns a yesterday's date.
 */
export function getYesterday(): ISODateString {
    const date = dateToString(addDaysToDate(new Date(), -1));
    if (date) {
        return date;
    }
    throw new Error("Date is null");
}

/**
 * Returns the date X days ago.
 */
export function getXDaysAgo(daysAgo: number): ISODateString {
    const date = dateToString(addDaysToDate(new Date(), -1 * daysAgo));
    if (date) {
        return date;
    }
    throw new Error("Date is null");
}

/**
 * Returns a date that is {daysToAdd} beyond {date}.
 */
export function addDaysToDate(date: Date, daysToAdd: number): Date {
    const dateCopy = new Date(date.getTime());
    dateCopy.setDate(date.getDate() + daysToAdd);

    return dateCopy;
}

/**
 * Returns a date that is {daysToAdd} beyond {date}.
 */
export function addDaysToISODateString(
    date: ISODateString,
    daysToAdd: number
): ISODateString {
    const dateFromISOString = getDateFromString(date);
    const dateCopy = new Date(dateFromISOString.getTime());
    dateCopy.setDate(dateFromISOString.getDate() + daysToAdd);

    const dateString = dateToString(dateCopy);
    if (dateString) {
        return dateString;
    }
    throw new Error("Date is null");
}

/**
 * Returns a string of a given date (string) without a timestamp.
 */
export function formatDateString(
    date: ISODateString | null
): ISODateString | null { 

    if (date)
    {
        const dateString = getDateFromString(date);
        const dateWithoutTimestamp = dateToString(dateString);
        return dateWithoutTimestamp;
    }
    return null;
}